import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const CHANNELS = ['WHATSAPP', 'EMAIL', 'SMS', 'INTERNO'];
const ACTION_TYPES = ['REMINDER', 'ALERTA', 'NEGOCIAR', 'ULTIMATO'];

const newStep = (ordem = 1) => ({
  ordem,
  day_offset: 0,
  canal: 'WHATSAPP',
  action_type: 'REMINDER',
  template_id: '',
  mensagem_padrao: '',
  ativo: true,
});

const CollectionsStrategiesPage = ({ initialSection = 'templates' }) => {
  const [templates, setTemplates] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const isTemplatesView = initialSection === 'templates';
  const viewTitle = isTemplatesView ? 'Templates de Mensagem' : 'Reguas de Recuperacao';
  const viewDescription = isTemplatesView
    ? 'Cadastre mensagens reutilizaveis por canal para usar nos fluxos de cobranca.'
    : 'Defina quando, por qual canal e com qual prioridade cada acao de recuperacao deve acontecer.';

  const [templateForm, setTemplateForm] = useState({
    canal: 'WHATSAPP',
    nome: '',
    assunto: '',
    corpo: '',
    ativo: true,
  });

  const [policyForm, setPolicyForm] = useState({
    nome: '',
    descricao: '',
    escopo_source_modules: 'ALL',
    min_dias_atraso: -3,
    max_dias_atraso: '',
    prioridade: 100,
    ativo: true,
    steps: [newStep(1)],
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [templateResponse, policyResponse] = await Promise.all([
        axios.get('/api/collections/templates'),
        axios.get('/api/collections/policies'),
      ]);
      setTemplates(Array.isArray(templateResponse?.data?.items) ? templateResponse.data.items : []);
      setPolicies(Array.isArray(policyResponse?.data?.items) ? policyResponse.data.items : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar estrategias');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const saveTemplate = async () => {
    try {
      if (!templateForm.nome.trim() || !templateForm.corpo.trim()) {
        toast.error('Nome e corpo do template sao obrigatorios');
        return;
      }
      await axios.post('/api/collections/templates', {
        canal: templateForm.canal,
        nome: templateForm.nome.trim(),
        assunto: templateForm.assunto.trim() || null,
        corpo: templateForm.corpo.trim(),
        ativo: templateForm.ativo,
      });
      toast.success('Template criado');
      setTemplateForm({
        canal: templateForm.canal,
        nome: '',
        assunto: '',
        corpo: '',
        ativo: true,
      });
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao criar template');
    }
  };

  const toggleTemplate = async (item) => {
    try {
      await axios.put(`/api/collections/templates/${item.id}`, {
        ativo: !item.ativo,
      });
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao atualizar template');
    }
  };

  const deleteTemplate = async (item) => {
    const confirmed = window.confirm(`Excluir template "${item.nome}"?`);
    if (!confirmed) return;
    try {
      await axios.delete(`/api/collections/templates/${item.id}`);
      toast.success('Template excluido');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao excluir template');
    }
  };

  const setStep = (index, patch) => {
    setPolicyForm((old) => ({
      ...old,
      steps: old.steps.map((step, i) => (i === index ? { ...step, ...patch } : step)),
    }));
  };

  const addStep = () => {
    setPolicyForm((old) => ({
      ...old,
      steps: [...old.steps, newStep(old.steps.length + 1)],
    }));
  };

  const removeStep = (index) => {
    setPolicyForm((old) => {
      const nextSteps = old.steps.filter((_, i) => i !== index).map((step, idx) => ({ ...step, ordem: idx + 1 }));
      return {
        ...old,
        steps: nextSteps.length > 0 ? nextSteps : [newStep(1)],
      };
    });
  };

  const savePolicy = async () => {
    try {
      if (!policyForm.nome.trim()) {
        toast.error('Nome da politica e obrigatorio');
        return;
      }
      if (!Array.isArray(policyForm.steps) || policyForm.steps.length === 0) {
        toast.error('Informe ao menos um passo');
        return;
      }

      const payload = {
        nome: policyForm.nome.trim(),
        descricao: policyForm.descricao.trim() || null,
        escopo_source_modules: [policyForm.escopo_source_modules || 'ALL'],
        min_dias_atraso: Number(policyForm.min_dias_atraso || 0),
        max_dias_atraso: policyForm.max_dias_atraso === '' ? null : Number(policyForm.max_dias_atraso),
        prioridade: Number(policyForm.prioridade || 100),
        ativo: policyForm.ativo,
        steps: policyForm.steps.map((step, index) => ({
          ordem: index + 1,
          day_offset: Number(step.day_offset || 0),
          canal: step.canal,
          action_type: step.action_type,
          template_id: step.template_id || null,
          mensagem_padrao: step.mensagem_padrao || null,
          ativo: step.ativo !== false,
        })),
      };

      await axios.post('/api/collections/policies', payload);
      toast.success('Politica criada');
      setPolicyForm({
        nome: '',
        descricao: '',
        escopo_source_modules: policyForm.escopo_source_modules,
        min_dias_atraso: -3,
        max_dias_atraso: '',
        prioridade: 100,
        ativo: true,
        steps: [newStep(1)],
      });
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao criar politica');
    }
  };

  const bootstrapDefaultPolicy = async () => {
    try {
      const { data } = await axios.post('/api/collections/policies/bootstrap-default');
      if (data?.already_exists) {
        toast.success('Regua padrao ja existia e foi mantida');
      } else {
        toast.success('Regua padrao criada com sucesso');
      }
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao criar regua padrao');
    }
  };

  const togglePolicy = async (policy) => {
    try {
      await axios.put(`/api/collections/policies/${policy.id}`, {
        ativo: !policy.ativo,
      });
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao atualizar politica');
    }
  };

  const disablePolicy = async (policy) => {
    const confirmed = window.confirm(`Desativar politica "${policy.nome}"?`);
    if (!confirmed) return;
    try {
      await axios.delete(`/api/collections/policies/${policy.id}`);
      toast.success('Politica desativada');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao desativar politica');
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      <section className="rounded-2xl border border-teal-300 bg-gradient-to-r from-slate-900 via-teal-900 to-emerald-900 p-6 text-white shadow-soft">
        <h1 className="text-3xl font-bold">{viewTitle}</h1>
        <p className="mt-2 text-teal-100">
          {viewDescription}
        </p>
        <div className="mt-4">
          <Link to="/cobrancas/recuperacao/canais" className="btn-secondary">
            Configurar Canais de Disparo
          </Link>
        </div>
      </section>

      <div className="flex flex-wrap gap-2">
        <Link
          to="/cobrancas/recuperacao/templates"
          className={`btn-secondary ${initialSection === 'templates' ? 'ring-2 ring-primary-200' : ''}`}
        >
          Templates
        </Link>
        <Link
          to="/cobrancas/recuperacao/estrategias"
          className={`btn-secondary ${initialSection === 'policies' ? 'ring-2 ring-primary-200' : ''}`}
        >
          Reguas
        </Link>
      </div>

      {isTemplatesView && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div className="card">
            <div className="card-header">Novo template de mensagem</div>
            <div className="card-body space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <select className="form-select" value={templateForm.canal} onChange={(e) => setTemplateForm((old) => ({ ...old, canal: e.target.value }))}>
                  {CHANNELS.map((channel) => (
                    <option key={channel} value={channel}>{channel}</option>
                  ))}
                </select>
                <input className="form-input" placeholder="Nome do template" value={templateForm.nome} onChange={(e) => setTemplateForm((old) => ({ ...old, nome: e.target.value }))} />
              </div>
              <input className="form-input" placeholder="Assunto (opcional)" value={templateForm.assunto} onChange={(e) => setTemplateForm((old) => ({ ...old, assunto: e.target.value }))} />
              <textarea className="form-textarea" rows={5} placeholder="Corpo (use {{cliente_nome}}, {{valor_aberto}}, {{dias_atraso}})" value={templateForm.corpo} onChange={(e) => setTemplateForm((old) => ({ ...old, corpo: e.target.value }))} />
              <button className="btn-primary" onClick={saveTemplate}>
                <PlusIcon className="h-5 w-5 mr-2" />
                Salvar template
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-header flex items-center justify-between">
              <span>Templates cadastrados</span>
              {loading && <div className="loader h-5 w-5" />}
            </div>
            <div className="card-body space-y-2 max-h-[28rem] overflow-y-auto">
              {templates.map((item) => (
                <div key={item.id} className="rounded-lg border border-gray-200 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium">{item.nome}</p>
                      <p className="text-xs text-gray-500">{item.canal}</p>
                    </div>
                    <span className={item.ativo ? 'badge-success' : 'badge-danger'}>{item.ativo ? 'Ativo' : 'Inativo'}</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-2 line-clamp-3">{item.corpo}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button className="btn-secondary text-xs py-1.5 px-2" onClick={() => toggleTemplate(item)}>
                      {item.ativo ? 'Desativar' : 'Ativar'}
                    </button>
                    <button className="btn-danger text-xs py-1.5 px-2" onClick={() => deleteTemplate(item)}>
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Excluir
                    </button>
                  </div>
                </div>
              ))}
              {templates.length === 0 && <p className="text-sm text-gray-500">Nenhum template cadastrado.</p>}
            </div>
          </div>
        </div>
      )}

      {!isTemplatesView && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div className="card">
            <div className="card-header">Nova politica de recuperacao</div>
            <div className="card-body space-y-3">
              <button className="btn-secondary" onClick={bootstrapDefaultPolicy}>
                Criar Regua Padrao D-3/D+1/D+5/D+10
              </button>
              <input className="form-input" placeholder="Nome da politica" value={policyForm.nome} onChange={(e) => setPolicyForm((old) => ({ ...old, nome: e.target.value }))} />
              <textarea className="form-textarea" rows={2} placeholder="Descricao" value={policyForm.descricao} onChange={(e) => setPolicyForm((old) => ({ ...old, descricao: e.target.value }))} />
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <select className="form-select" value={policyForm.escopo_source_modules} onChange={(e) => setPolicyForm((old) => ({ ...old, escopo_source_modules: e.target.value }))}>
                  <option value="ALL">ALL</option>
                  <option value="CORE">CORE</option>
                  <option value="SOLAR">SOLAR</option>
                </select>
                <input className="form-input" type="number" placeholder="Min atraso" value={policyForm.min_dias_atraso} onChange={(e) => setPolicyForm((old) => ({ ...old, min_dias_atraso: e.target.value }))} />
                <input className="form-input" type="number" placeholder="Max atraso" value={policyForm.max_dias_atraso} onChange={(e) => setPolicyForm((old) => ({ ...old, max_dias_atraso: e.target.value }))} />
                <input className="form-input" type="number" placeholder="Prioridade" value={policyForm.prioridade} onChange={(e) => setPolicyForm((old) => ({ ...old, prioridade: e.target.value }))} />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">Passos da politica</p>
                  <button className="btn-secondary text-xs py-1.5 px-2" onClick={addStep}>
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Adicionar passo
                  </button>
                </div>

                {policyForm.steps.map((step, index) => (
                  <div key={`step-${index}`} className="rounded-lg border border-gray-200 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold">Passo {index + 1}</p>
                      <button className="btn-danger text-xs py-1 px-2" onClick={() => removeStep(index)}>
                        Remover
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <input className="form-input" type="number" placeholder="Dia offset" value={step.day_offset} onChange={(e) => setStep(index, { day_offset: e.target.value })} />
                      <select className="form-select" value={step.canal} onChange={(e) => setStep(index, { canal: e.target.value })}>
                        {CHANNELS.map((channel) => (
                          <option key={channel} value={channel}>{channel}</option>
                        ))}
                      </select>
                      <select className="form-select" value={step.action_type} onChange={(e) => setStep(index, { action_type: e.target.value })}>
                        {ACTION_TYPES.map((actionType) => (
                          <option key={actionType} value={actionType}>{actionType}</option>
                        ))}
                      </select>
                      <select className="form-select" value={step.template_id} onChange={(e) => setStep(index, { template_id: e.target.value })}>
                        <option value="">Sem template</option>
                        {templates.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.nome}
                          </option>
                        ))}
                      </select>
                    </div>
                    <textarea className="form-textarea" rows={2} placeholder="Mensagem padrao (fallback)" value={step.mensagem_padrao} onChange={(e) => setStep(index, { mensagem_padrao: e.target.value })} />
                  </div>
                ))}
              </div>

              <button className="btn-primary" onClick={savePolicy}>Salvar politica</button>
            </div>
          </div>

          <div className="card">
            <div className="card-header">Politicas cadastradas</div>
            <div className="card-body space-y-3 max-h-[32rem] overflow-y-auto">
              {policies.map((policy) => (
                <div key={policy.id} className="rounded-lg border border-gray-200 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium">{policy.nome}</p>
                      <p className="text-xs text-gray-500">
                        Escopo: {(policy.escopo_source_modules || []).join(', ')} | Prioridade: {policy.prioridade}
                      </p>
                    </div>
                    <span className={policy.ativo ? 'badge-success' : 'badge-danger'}>{policy.ativo ? 'Ativa' : 'Inativa'}</span>
                  </div>
                  <div className="mt-2 text-xs text-gray-600">
                    {(policy.steps || []).map((step) => (
                      <p key={step.id}>
                        #{step.ordem} D{step.day_offset >= 0 ? '+' : ''}{step.day_offset} - {step.action_type} via {step.canal}
                      </p>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button className="btn-secondary text-xs py-1.5 px-2" onClick={() => togglePolicy(policy)}>
                      {policy.ativo ? 'Desativar' : 'Ativar'}
                    </button>
                    <button className="btn-danger text-xs py-1.5 px-2" onClick={() => disablePolicy(policy)}>
                      Desativar com passos
                    </button>
                  </div>
                </div>
              ))}
              {policies.length === 0 && <p className="text-sm text-gray-500">Nenhuma politica cadastrada.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionsStrategiesPage;
